import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, Pencil, MapPin, Target, Zap, BookOpen } from "lucide-react";
import { QuickCampaignDialog } from "@/components/QuickCampaignDialog";
import { TemplatesDialog } from "@/components/TemplatesDialog";

const STYLES = { direct: "Direct et commercial", premium: "Premium et institutionnel", chaleureux: "Chaleureux et local" };

export default function Campaigns() {
  const { user } = useAuth();
  const canWrite = ["admin", "responsable_commercial", "commercial"].includes(user?.role);
  const canDelete = ["admin", "responsable_commercial"].includes(user?.role);
  const [campaigns, setCampaigns] = useState(null);
  const [showQuick, setShowQuick] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const load = () => api.get("/campaigns").then(({ data }) => setCampaigns(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la campagne « ${c.nom} » ?`)) return;
    try {
      await api.delete(`/campaigns/${c.id}`);
      toast.success("Campagne supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="campaigns-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold sm:text-4xl">Campagnes</h1>
          <p className="mt-1 text-sm text-muted-foreground">Une campagne reste en brouillon tant qu'elle n'est pas validée.</p>
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setShowQuick(true)} className="bg-accent hover:bg-accent/90" data-testid="quick-campaign-button">
              <Zap size={16} className="mr-1.5" /> Création rapide par IA
            </Button>
            <Button variant="outline" onClick={() => setShowTemplates(true)} data-testid="templates-button">
              <BookOpen size={16} className="mr-1.5" /> Bibliothèque métiers
            </Button>
            <Button asChild variant="outline" data-testid="new-campaign-button">
              <Link to="/campagnes/nouvelle"><Plus size={16} className="mr-1.5" /> Créer manuellement</Link>
            </Button>
          </div>
        )}
      </div>

      {campaigns === null ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : campaigns.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">Aucune campagne. Créez votre première campagne de prospection.</CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="campaigns-list">
          {campaigns.map((c) => (
            <Card key={c.id} className="kpi-card">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-display text-lg font-semibold">{c.nom}</p>
                    <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{c.service_vendu || "Service non renseigné"}</p>
                  </div>
                  <Badge variant={c.statut === "validee" ? "default" : "secondary"} className={c.statut === "validee" ? "bg-accent" : ""} data-testid={`campaign-status-${c.id}`}>
                    {c.statut === "validee" ? "Validée" : "Brouillon"}
                  </Badge>
                </div>
                <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                  <p className="flex items-center gap-2"><Target size={14} /> {(c.secteurs || []).slice(0, 3).join(", ") || "Secteurs non définis"}</p>
                  <p className="flex items-center gap-2"><MapPin size={14} /> {[...(c.villes || []), ...(c.departements || []).map((d) => `Dép. ${d}`)].slice(0, 3).join(", ") || "Zone non définie"}</p>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span>{c.volume_quotidien_max ?? "—"} contacts/jour max</span>·
                  <span>{c.nb_relances_max ?? "—"} relances</span>·
                  <span>{STYLES[c.style_message] || c.style_message}</span>
                </div>
                <div className="mt-4 flex gap-2">
                  {canWrite && (
                    <Button asChild size="sm" variant="outline" data-testid={`edit-campaign-${c.id}`}>
                      <Link to={`/campagnes/${c.id}`}><Pencil size={14} className="mr-1.5" /> Ouvrir</Link>
                    </Button>
                  )}
                  {canDelete && (
                    <Button size="sm" variant="ghost" onClick={() => remove(c)} data-testid={`delete-campaign-${c.id}`}>
                      <Trash2 size={14} className="text-destructive" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <QuickCampaignDialog open={showQuick} onClose={() => setShowQuick(false)} />
      <TemplatesDialog open={showTemplates} onClose={() => setShowTemplates(false)} />
    </div>
  );
}
